import { useState } from 'react';
import { IconFlash, IconPlus } from '../components/icons';
import { RowActions } from '../components/RowActions';
import { Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, Toggle, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * Versorger (Objekt-Check, Kap. 7).
 *
 * Eigenständig von den Dienstleistern, obwohl strukturell ähnlich: Ein
 * Versorger beliefert ein Objekt (Strom/Wasser/Gas/Fernwärme/Öl/Pellets),
 * kein Dienstleister erbringt eine Leistung. Die Tarife dazu stehen auf einer
 * eigenen Seite, weil ein Versorger mehrere Tarife für unterschiedliche
 * Sparten führen kann.
 */
export function UtilitySuppliersPage() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [modal, setModal] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const suppliers = useAsync(
    () => api.get('/api/utilities/suppliers', { includeInactive: showInactive ? 'true' : '' }),
    [showInactive],
  );

  const mayManage = can('utilities.manage');
  const list: any[] = suppliers.data ?? [];

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconFlash width={22} height={22} />}
        title="Versorger"
        subtitle="Strom-, Wasser-, Gas- und Wärmelieferanten mit Kontaktdaten – Grundlage für die Versorgungstarife."
        pill="Geschäftspartner"
        tone="info"
        actions={
          mayManage ? (
            <button className="btn primary" onClick={() => setModal('neu')}>
              <IconPlus width={15} height={15} /> Versorger anlegen
            </button>
          ) : undefined
        }
      />

      <Card noBody>
        <div className="card-body tight">
          <label className="checkbox-row">
            <input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />
            Stillgelegte anzeigen
          </label>
        </div>
      </Card>

      {suppliers.loading && !suppliers.data ? (
        <Loading />
      ) : list.length === 0 ? (
        <Card>
          <EmptyState icon={<IconFlash width={20} height={20} />} title="Kein Versorger hinterlegt" />
        </Card>
      ) : (
        <Card noBody>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Versorger</th>
                  <th>Ansprechpartner</th>
                  <th>Anschrift</th>
                  {mayManage && <th className="actions">Aktionen</th>}
                </tr>
              </thead>
              <tbody>
                {list.map((supplier: any) => (
                  <tr key={supplier.id} style={supplier.is_active ? undefined : { opacity: 0.55 }}>
                    <td>
                      <strong>{supplier.name}</strong>
                      {supplier.note && <div className="small subtle truncate" style={{ maxWidth: 260 }}>{supplier.note}</div>}
                    </td>
                    <td className="small subtle">
                      {supplier.contact_name ?? '–'}
                      {supplier.contact_phone ? ` · ${supplier.contact_phone}` : ''}
                      {supplier.contact_email ? ` · ${supplier.contact_email}` : ''}
                    </td>
                    <td className="small subtle">
                      {[supplier.street, [supplier.zip, supplier.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '–'}
                    </td>
                    {mayManage && (
                      <td className="actions">
                        {supplier.is_active ? (
                          <RowActions
                            label={supplier.name}
                            onEdit={() => setModal(`bearbeiten-${supplier.id}`)}
                            deletePath={`/api/utilities/suppliers/${supplier.id}${
                              supplier.tariff_count > 0 ? '' : '?permanent=true'
                            }`}
                            confirmText={
                              supplier.tariff_count > 0
                                ? `An „${supplier.name}“ hängen ${supplier.tariff_count} Tarif(e). Der Eintrag wird stillgelegt und bleibt als Beleg erhalten. Fortfahren?`
                                : `Versorger „${supplier.name}“ löschen?`
                            }
                            onDeleted={() => suppliers.reload()}
                          />
                        ) : (
                          <button
                            className="btn sm"
                            onClick={async () => {
                              await api.post(`/api/utilities/suppliers/${supplier.id}/reactivate`);
                              notify('Versorger wieder aktiv.', 'success');
                              suppliers.reload();
                            }}
                          >
                            Reaktivieren
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {(modal === 'neu' || modal?.startsWith('bearbeiten-')) && (
        <SupplierModal
          supplier={list.find((entry: any) => String(entry.id) === modal?.replace('bearbeiten-', ''))}
          onClose={() => setModal(null)}
          onSaved={() => {
            const wasNew = modal === 'neu';
            setModal(null);
            suppliers.reload();
            notify(wasNew ? 'Versorger angelegt.' : 'Versorger gespeichert.', 'success');
          }}
        />
      )}
    </div>
  );
}

function SupplierModal({
  supplier,
  onClose,
  onSaved,
}: {
  supplier?: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: supplier?.name ?? '',
    contactName: supplier?.contact_name ?? '',
    contactEmail: supplier?.contact_email ?? '',
    contactPhone: supplier?.contact_phone ?? '',
    street: supplier?.street ?? '',
    zip: supplier?.zip ?? '',
    city: supplier?.city ?? '',
    note: supplier?.note ?? '',
    isActive: supplier ? !!supplier.is_active : true,
  });
  const [error, setError] = useState<unknown>(null);
  const set = (key: keyof typeof form, value: any) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      title={supplier ? `Versorger bearbeiten – ${supplier.name}` : 'Versorger anlegen'}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={form.name.trim().length < 2}
            onClick={async () => {
              try {
                const payload = {
                  ...form,
                  contactName: form.contactName || null,
                  contactEmail: form.contactEmail || null,
                  contactPhone: form.contactPhone || null,
                  street: form.street || null,
                  zip: form.zip || null,
                  city: form.city || null,
                  note: form.note || null,
                };
                if (supplier) await api.patch(`/api/utilities/suppliers/${supplier.id}`, payload);
                else await api.post('/api/utilities/suppliers', payload);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Name" required>
            <input value={form.name} autoFocus onChange={(event) => set('name', event.target.value)} />
          </Field>
          <Field label="Ansprechpartner">
            <input value={form.contactName} onChange={(event) => set('contactName', event.target.value)} />
          </Field>
          <Field label="Telefon">
            <input value={form.contactPhone} onChange={(event) => set('contactPhone', event.target.value)} />
          </Field>
          <Field label="E-Mail">
            <input type="email" value={form.contactEmail} onChange={(event) => set('contactEmail', event.target.value)} />
          </Field>
          <Field label="Straße">
            <input value={form.street} onChange={(event) => set('street', event.target.value)} />
          </Field>
          <Field label="PLZ">
            <input value={form.zip} onChange={(event) => set('zip', event.target.value)} />
          </Field>
          <Field label="Ort">
            <input value={form.city} onChange={(event) => set('city', event.target.value)} />
          </Field>
        </div>
        <Field label="Bemerkung">
          <input value={form.note} onChange={(event) => set('note', event.target.value)} />
        </Field>
        <Toggle checked={form.isActive} onChange={(value) => set('isActive', value)} label="Aktiv" />
      </div>
    </Modal>
  );
}
